/**
 * Provider auto-detection from a Square service name string.
 *
 * Karine escalation (Sep 19 2026 · Oriana Laderoute): a Square catalog
 * service called "-Aesthetician Tanya FREE Consultation" was booked
 * onto Karine's calendar because the frontend didn't send
 * `provider: "tanya"`. This helper is the single source of truth for
 * "does this service name imply a specific provider?".
 *
 * Mirrors backend `_KARINE_FORBIDDEN_KEYWORDS` so the frontend routes
 * correctly on the FIRST attempt and never triggers the 422 safety
 * net. Any change here should be mirrored in `backend/server.py`
 * `_KARINE_FORBIDDEN_DEFAULT`.
 *
 * @param {string|null|undefined} serviceName  raw Square service name
 * @returns {"tanya" | "linda" | null}  detected provider, or null
 */
export function providerFromServiceName(serviceName) {
  if (!serviceName || typeof serviceName !== "string") return null;
  const n = serviceName.toLowerCase();

  // Linda keywords — she's the only NP, her services are unique.
  const lindaHints = [
    "aesthetician linda",
    "with linda",
    "linda free consult",
    "linda consult",
    "linda's",
    "linda\u2019s",   // Unicode right single quotation mark (Square uses this)
    "np linda",
    "— linda",
    "- linda",
  ];
  if (lindaHints.some((k) => n.includes(k))) return "linda";

  // Tanya keywords
  const tanyaHints = [
    "aesthetician tanya",
    "with tanya",
    "tanya free consult",
    "tanya consult",
    "tanya's",
    "tanya\u2019s",   // Unicode right single quotation mark
    "— tanya",
    "- tanya",
    "by tanya",
  ];
  if (tanyaHints.some((k) => n.includes(k))) return "tanya";

  // Sep 28 2026 · Karine escalation ("Fix it!!!"): aesthetician-only
  // service keywords must ALSO route to Tanya on the client side.
  // Mirror of backend `_KARINE_FORBIDDEN_DEFAULT`. Without this,
  // services like "Fall PROMO Laser Genesis + Lymphatic Facial
  // Drain" (which don't mention Tanya by name) got booked with
  // `provider=null` → backend defaulted to Karine → 422 salvage
  // alert. Now the client sends `provider=tanya` from the first
  // attempt.
  const aestheticianHints = [
    "laser genesis",
    "lymphatic",
    "hydrafacial",
    "hydra facial",
    "brow lamination",
    "lash lift",
    // Nov 2 2026 · Lash & Brow Studio menu (Tanya · Ottawa)
    "lash tint",
    "brow tint",
    "brow shaping",
    "eye makeover",
    "everyday definition",
    "lift & define",
    "brow makeover",
    "dermaplan",
    "chemical peel",
    "microneedling",
    "micro needling",
    "eminence",
    "\u00e9minence",   // "Éminence" with accent
    "aveda",
    "dermalinfusion",
    "sono peel",
    // Oct 20 2026 · Karine escalation — IPL Photofacial booked onto Karine.
    "ipl",
    "photofacial",
    "photo facial",
    "laser hair",
    "laser \u2014",
    "laser \u2013",
    "laser \u00b7",
    "unlimited laser",
    "head to toe",
    "skin tightening",
    "potenza",
  ];
  if (aestheticianHints.some((k) => n.includes(k))) return "tanya";

  return null;
}

/**
 * Given a cart of selected services (array of objects with `name`
 * or `service_name` + optional `ottawa_only` flag), returns the
 * provider slug that should be sent to the backend.
 *
 * Priority: explicit Linda-named beats Tanya-named beats
 * `ottawa_only` flag beats null (which the backend treats as Karine).
 */
export function providerFromSelectedServices(services) {
  if (!Array.isArray(services) || services.length === 0) return null;
  let sawTanya = false;
  let allOttawaOnly = true;
  for (const s of services) {
    const nm = s?.name || s?.service_name || "";
    const hint = providerFromServiceName(nm);
    if (hint === "linda") return "linda";
    if (hint === "tanya") sawTanya = true;
    if (!s?.ottawa_only) allOttawaOnly = false;
  }
  if (sawTanya) return "tanya";
  if (allOttawaOnly) return "tanya";
  return null;
}
